import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Holiday, WorkingDaysConfig, getWorkingDaysConfig, isWorkingDay } from '@/services/holidays';

interface HolidayCalendarProps {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  minDate?: string;
  className?: string;
}

// Formatea fecha local a YYYY-MM-DD sin sesgo UTC
const formatDateLocal = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const HolidayCalendar: React.FC<HolidayCalendarProps> = ({ 
  selectedDate, 
  onDateSelect, 
  minDate = formatDateLocal(new Date()),
  className = ''
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = selectedDate ? new Date(selectedDate) : new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  const [workingDaysConfig, setWorkingDaysConfig] = useState<WorkingDaysConfig>({
    workingDays: [1, 2, 3, 4, 5], // Por defecto: Lunes a Viernes
    holidays: []
  });

  const [loading, setLoading] = useState(true);

  // Cargar configuración de días laborales al montar el componente
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        const config = await getWorkingDaysConfig();
        setWorkingDaysConfig(config);
      } catch (error) {
        console.error('Error loading working days config:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  // Re-sincronizar el mes actual cuando cambia la fecha seleccionada desde fuera
  useEffect(() => {
    if (selectedDate) {
      const date = new Date(selectedDate);
      if (!isNaN(date.getTime())) {
        setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
      }
    }
  }, [selectedDate]);

  const today = new Date();
  const [minYear, minMonth, minDay] = minDate.split('-').map(Number);
  const minDateObj = new Date(minYear, (minMonth || 1) - 1, minDay || 1);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isDateDisabled = (date: Date) => {
    return date < minDateObj || !isWorkingDay(date, workingDaysConfig);
  };

  const isDateSelected = (date: Date) => {
    return selectedDate === formatDateLocal(date);
  };

  const isToday = (date: Date) => {
    return date.toDateString() === today.toDateString();
  };

  const isHoliday = (date: Date) => {
    const dateString = formatDateLocal(date);
    return workingDaysConfig.holidays.some(holiday => holiday.date === dateString);
  };

  const getHolidayInfo = (date: Date) => {
    const dateString = formatDateLocal(date);
    return workingDaysConfig.holidays.find(holiday => holiday.date === dateString);
  };

  const handleDateClick = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (!isDateDisabled(date)) {
      onDateSelect(formatDateLocal(date));
    }
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Días del mes anterior (para completar la primera semana)
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`prev-${i}`} className="h-10 w-full"></div>
      );
    }

    // Días del mes actual
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const disabled = isDateDisabled(date);
      const selected = isDateSelected(date);
      const today = isToday(date);
      const holiday = isHoliday(date);
      const holidayInfo = getHolidayInfo(date);

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          disabled={disabled}
          title={holidayInfo ? holidayInfo.name : undefined}
          className={`
            h-10 w-full flex items-center justify-center text-sm font-medium rounded-lg transition-all duration-200 relative
            ${disabled 
              ? holiday
                ? 'text-red-400 bg-red-50 cursor-not-allowed border border-red-200'
                : 'text-gray-300 cursor-not-allowed'
              : selected
                ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-md transform hover:scale-105'
                : today
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300 shadow-sm'
                  : 'text-gray-700 hover:bg-gray-100 hover:shadow-sm'
            }
          `}
        >
          {day}
          {holiday && (
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></div>
          )}
        </button>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl shadow-xl border border-gray-200 p-8 ${className}`}>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Cargando calendario...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-2xl shadow-xl border border-gray-200 ${className}`}>
      {/* Header del calendario */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <button
          onClick={goToPreviousMonth}
          className="p-2 hover:bg-white hover:shadow-md rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={currentMonth <= minDateObj}
        >
          <ChevronLeftIcon className="w-5 h-5 text-blue-600" />
        </button>
        
        <h3 className="text-lg font-semibold text-blue-800">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        
        <button
          onClick={goToNextMonth}
          className="p-2 hover:bg-white hover:shadow-md rounded-lg transition-all duration-200"
        >
          <ChevronRightIcon className="w-5 h-5 text-blue-600" />
        </button>
      </div>

      {/* Días de la semana */}
      <div className="grid grid-cols-7 gap-1 p-3 bg-gray-50">
        {dayNames.map(day => (
          <div key={day} className="h-8 flex items-center justify-center text-xs font-semibold text-gray-600 uppercase tracking-wide">
            {day}
          </div>
        ))}
      </div>

      {/* Días del calendario */}
      <div className="grid grid-cols-7 gap-1 p-3">
        {renderCalendarDays()}
      </div>

      {/* Leyenda */}
      <div className="p-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="flex items-center justify-center space-x-4 text-xs text-gray-600">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full shadow-sm"></div>
            <span className="font-medium">Seleccionado</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded-full"></div>
            <span className="font-medium">Hoy</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-50 border border-red-200 rounded-full relative">
              <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-red-500 rounded-full"></div>
            </div>
            <span className="font-medium">Festivo</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
            <span className="font-medium">No disponible</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HolidayCalendar;
